import { create } from 'zustand';
import api, { invalidateApiRequests } from './api';
import { useStore } from '../store';
import {
  createSessionCoordinator,
  trialError,
  type SessionState,
} from './sessionFlow';

export const useSession = create<{
  identity: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error' | 'expired';
  state: SessionState | null;
  error: unknown;
}>(() => ({ identity: null, status: 'idle', state: null, error: null }));

let generation = 0;
function invalidateSessionRequests() {
  generation++;
  invalidateApiRequests();
}

const coordinator = createSessionCoordinator({
  read: async () => (await api.get<SessionState>('/api/session')).data,
  bootstrap: async () => (await api.post<SessionState>('/api/session')).data,
  claim: async () =>
    (await api.post<{ claimed: boolean }>('/api/session/claim')).data,
  load: async checkIdentity => {
    await useStore.getState().fetchUser();
    checkIdentity();
    await useStore.getState().fetchScans();
  },
  begin: () => {
    invalidateSessionRequests();
    useStore.getState().clearIdentity();
    useSession.setState({ status: 'loading', state: null, error: null });
  },
  complete: state =>
    useSession.setState({ status: 'ready', state, error: null }),
  fail: error =>
    useSession.setState({
      status:
        trialError(error).code === 'anonymous_session_expired'
          ? 'expired'
          : 'error',
      error,
    }),
  lock: async work => {
    if (typeof navigator !== 'undefined' && navigator.locks)
      return await navigator.locks.request(
        'ce-scan-session-bootstrap',
        async () => await work()
      );
    return await work();
  },
});
export function startSession(identity: string) {
  if (useSession.getState().identity !== identity) {
    useSession.setState({
      identity,
      status: 'loading',
      state: null,
      error: null,
    });
  }
  return coordinator.start(identity);
}
export function invalidateSession() {
  invalidateSessionRequests();
  coordinator.invalidate();
  useStore.getState().clearIdentity();
  useSession.setState({
    identity: null,
    status: 'idle',
    state: null,
    error: null,
  });
}
export function expireSession() {
  invalidateSessionRequests();
  coordinator.invalidate();
  useStore.getState().clearIdentity();
  useSession.setState({ status: 'expired', state: null });
}
export async function refreshSession() {
  const current = generation;
  if (useSession.getState().status !== 'ready') return;
  try {
    const response = await api.get<SessionState>('/api/session');
    if (current === generation && useSession.getState().status === 'ready')
      useSession.setState({ state: response.data });
  } catch (error) {
    if (
      current === generation &&
      (error as { response?: { status?: number } }).response?.status === 401
    ) {
      invalidateSessionRequests();
      coordinator.invalidate();
      useStore.getState().clearIdentity();
      const expired =
        trialError(error).code === 'anonymous_session_expired' ||
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error === 'missing session';
      useSession.setState({
        status: expired ? 'expired' : 'error',
        state: null,
        error,
      });
    }
    throw error;
  }
}
export async function resetExpiredSession(identity = 'anonymous') {
  invalidateSessionRequests();
  coordinator.invalidate();
  await api.post('/api/session/reset', undefined, { skipAuth: true });
  invalidateSession();
  return startSession(identity);
}
